import { useEffect, useState } from "react";
import { Icon } from "./Icon";

export interface PinPadProps {
  length?: number;
  value?: string;
  onChange?: (pin: string) => void;
  onComplete?: (pin: string) => void;
  label?: string;
  error?: string;
  disabled?: boolean;
}

/** Große Zifferntasten (80 px) für die Anmeldung am Kassengerät. Punkte zeigen den Fortschritt, nie die Ziffern. */
export function PinPad({ length = 4, value, onChange, onComplete, label = "PIN eingeben", error, disabled }: PinPadProps) {
  const [intern, setIntern] = useState("");
  const pin = value ?? intern;

  const setze = (neu: string) => {
    if (value === undefined) setIntern(neu);
    onChange?.(neu);
    if (neu.length === length) onComplete?.(neu);
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (disabled) return;
      if (/^\d$/.test(e.key) && pin.length < length) setze(pin + e.key);
      else if (e.key === "Backspace") setze(pin.slice(0, -1));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [pin, length, disabled]);

  useEffect(() => {
    if (error && value === undefined) setIntern("");
  }, [error, value]);

  const tasten = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "löschen", "0", "zurück"];
  return (
    <div className={["rg-pin", error && "rg-pin--error"].filter(Boolean).join(" ")}>
      <div className="rg-pin__label">{label}</div>
      <div className="rg-pin__dots" aria-label={`${pin.length} von ${length} Ziffern`} role="status">
        {Array.from({ length }, (_, i) => (
          <span key={i} className={["rg-pin__dot", i < pin.length && "is-on"].filter(Boolean).join(" ")} />
        ))}
      </div>
      {error ? (
        <p className="rg-pin__error" role="alert">
          <Icon name="circle-alert" size={18} /> {error}
        </p>
      ) : null}
      <div className="rg-pin__grid">
        {tasten.map((t) =>
          t === "löschen" ? (
            <button key={t} type="button" className="rg-pin__key rg-pin__key--fn" onClick={() => setze("")} disabled={disabled || !pin.length}>
              Löschen
            </button>
          ) : t === "zurück" ? (
            <button key={t} type="button" className="rg-pin__key rg-pin__key--fn" onClick={() => setze(pin.slice(0, -1))} disabled={disabled || !pin.length} aria-label="Letzte Ziffer löschen">
              <Icon name="delete" size={24} />
            </button>
          ) : (
            <button key={t} type="button" className="rg-pin__key" onClick={() => pin.length < length && setze(pin + t)} disabled={disabled}>
              {t}
            </button>
          ),
        )}
      </div>
    </div>
  );
}
